import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import { DateTime } from 'luxon'

import { CircularProgress } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import { Box, Tooltip } from '@mui/material'

import SingleTutor from '../tutors/SingleTutor'
import DoubleTutor from '../tutors/DoubleTutor'
import TripleTutor from '../tutors/TripleTutor'

import Welcome from './Welcome'

import useScheduleEvents from 'hooks/useScheduleEvents'

import { Event } from 'types/event.types'
import { QuadTutor } from '../tutors/QuadTutor'
import { FiveTutor } from '../tutors/FiveTutor'
import { extractNameParts } from 'utils/events.names'
import { useShiftsRealTimeUpdates } from 'controllers/shifts/useShifts'
import { getDefaultPlaceholderPhotoPath, getStudentPhotoPath } from 'utils/profiles'

import * as ShiftController from 'controllers/shifts/ShiftController'

import { useSignUps } from 'hooks/useSignUps'

import { jsonDump } from 'utils/debugging'
import { checkUrl } from 'utils/urls'
import { getVersionTooltip } from 'config/version'

import { SessionProfile, Student } from 'types/student.types'
import { ShiftEvent } from 'types/tutoringShifts.types'
import { getDraftBaseUrl } from 'utils/draftBase'
import { ShiftRow, ShiftStatus } from 'types/supabase.database.custom.types'

const useStyles = makeStyles(
  theme => ({
    root: {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      minHeight: '100%',
      minWidth: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      paddingBottom: theme.spacing(2), // pushes main content up slightly (fits better on TV display)
    },

    loading: {
      display: 'flex',
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: theme.spacing(1),
    },

    header: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: theme.spacing(2),
    },

    content: {
      // backgroundColor: 'lightblue',
    },

    tableContainer: {
      display: 'flex',
      // flex: 1,
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },

    tablePaper: {
      margin: theme.spacing(3),
    },

    // Tutor Display
    tutorContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minWidth: '100vw',
    },

    tutorDisplay: {
      display: 'flex',
      flex: 1,
      justifyContent: 'center',
      minWidth: '100vw',
    },
  }),
  { name: 'OnDuty' }
)

const OnDuty = (props: PageProps) => {
  const classes = useStyles(props)

  const { date, time } = useParams()

  const [inputDateTime, setInputDateTime] = useState<DateTime>(DateTime.local())

  const [loading, setLoading] = useState(true)

  const [dateTimeDisplay, setDateTimeDisplay] = useState('')

  const [tutors, setTutors] = useState<Student[]>([])

  const { data: signups } = useSignUps()

  console.log('SIGNUPS OK: ', signups)

  const [icsEvents, setIcsEvents] = useState<Event[]>([])

  const [currentEvents, setCurrentEvents] = useState<ShiftEvent[]>([]) // events happening now

  const [profiles, setProfiles] = useState<SessionProfile[]>([])
  const [profilesPrepared, setProfilesPrepared] = useState(false)

  // Get events happening at the input time: tutoring shifts from TA Draft
  const { events: eventsBetween, error: contentsError } = useScheduleEvents({
    from: inputDateTime.toISO() || '',
    to: inputDateTime.toISO() || '',
  })

  console.info('ON DUTY > useScheduleEvents > eventsBetween: ', eventsBetween)

  const updatedRow = useShiftsRealTimeUpdates()

  // Set inputDate on page load
  useEffect(() => {
    const inputTime = time ? time : '000000'

    const input =
      date && inputTime
        ? DateTime.fromFormat(`${date} ${inputTime}`, 'yyyyMMdd HHmmss')
        : DateTime.local()

    setInputDateTime(input)
  }, [date, time])

  // Set display time
  useEffect(() => {
    setDateTimeDisplay(inputDateTime.toLocaleString(DateTime.DATETIME_HUGE_WITH_SECONDS))
  }, [inputDateTime])

  // Using raw eventsBetween, filter for events happening now
  useEffect(() => {
    setIcsEvents(eventsBetween)

    // Filter the ICS events to find events happening now
    const currentEvents = eventsBetween.filter(event => {
      const start = DateTime.fromISO(event.startISO).toLocal().toISOTime() || ''
      const end = DateTime.fromISO(event.endISO).toLocal().toISOTime() || ''

      const now =
        date && time
          ? DateTime.fromFormat(`${date} ${time}`, 'yyyyMMdd HHmmss').toLocal().toISOTime() || ''
          : DateTime.local().toISOTime() || ''

      return start <= now && now < end
    })

    console.info('EFFECT > CURRENT EVENTS: ', currentEvents)
    setCurrentEvents(currentEvents)

    // Shifts from TA Draft carry the tutor's profile (name, class year, photo).  Only events
    // without one (from an older TA Draft) are looked up in the signups, so wait
    // for signups only then — running with empty signups causes every such tutor to fall back
    // to a placeholder student (lastName = last initial), which produces broken image URLs
    // like mannya_s.jpg instead of mannya_sinha.jpg.
    const needsSignups = currentEvents.some((event: ShiftEvent) => !event.profile)
    if (needsSignups && (!signups || signups.length === 0)) return

    // Process the currentEvents to create the tutors array based on student API data
    // IMPORTANT: Maintain 1:1 correspondence between events and tutors
    const tutors: Student[] = []
    for (const event of currentEvents) {
      const { firstName, lastInitial } = extractNameParts(event.summary) || {
        firstName: '',
        lastInitial: '',
      }

      const tutorProfile = (event as ShiftEvent).profile
      if (tutorProfile) {
        tutors.push({
          firstName: tutorProfile.firstName,
          // The single-tutor view shows the last name in full
          lastName: tutorProfile.lastName || lastInitial,
          nickname: tutorProfile.nickname,
          classOf: tutorProfile.classOf ?? 0,
          year: tutorProfile.year,
        })
        continue
      }

      const regex1 = new RegExp(firstName, 'i')
      const regex2 = new RegExp(`^${lastInitial}`, 'i')

      console.info('EFFECT > setTutors > regex1, regex2:', { regex1, regex2 })

      console.info('EFFECT > setTutors > signups:', signups)
      const results = signups?.filter(row => {
        const firstNameMatch = row.firstName && regex1.test(row.firstName)
        const nicknameMatch = row.nickname && regex1.test(row.nickname)
        const lastNameMatch = row.lastName && regex2.test(row.lastName)
        return (firstNameMatch || nicknameMatch) && lastNameMatch
      })
      console.info('EFFECT > setTutors > filtered signups > results:', results)

      if (results && results.length > 0) {
        const result = results[0]

        const student: Student = {
          firstName: result.firstName as string,
          lastName: result.lastName as string,
          nickname: result.nickname as string,
          classOf: result.classOf as number,
        }

        tutors.push(student)
      } else {
        // Push a placeholder tutor to maintain index alignment with events
        // Use the name from the event as fallback
        const student: Student = {
          firstName: firstName || 'Unknown',
          lastName: lastInitial || 'Tutor',
          nickname: '',
          classOf: 0,
        }
        tutors.push(student)
        console.warn(`No signup match found for ${firstName} ${lastInitial}., using event data as fallback`)
      }
    }
    console.info('TUTORS CREATED: ', tutors)
    setTutors(tutors)
  }, [eventsBetween, signups])

  // Connect tutors and events in profiles to avoid incorrect tutor/event data
  useEffect(() => {
    console.info('EFFECT > CREATE PROFILES ...')
    const createProfiles = async () => {
      const profiles: SessionProfile[] = []

      // Fetch all shifts for the day from Supabase (same approach as ListSessions)
      const startOfDay = inputDateTime.startOf('day').toISO() as string
      const endOfDay = inputDateTime.endOf('day').toISO() as string
      const todayShifts: ShiftRow[] = await ShiftController.fetchShiftsBetween(startOfDay, endOfDay)
      console.info('ON DUTY > TODAY SHIFTS FROM SUPABASE: ', todayShifts)

      console.info('LOOP OVER EVENTS: ', currentEvents)
      console.info('LOOP OVER TUTORS: ', tutors)
      for (let i = 0; i < tutors.length; i++) {
        const event: ShiftEvent = currentEvents[i]
        const tutor = tutors[i]

        console.info('CREATING PROFILE > tutor: ', tutor)

        let imageUrl = ''
        if (event.profile) {
          // TA Draft already looked the photo up (null when there is none on file)
          imageUrl = event.profile.photo
            ? `${await getDraftBaseUrl()}/${event.profile.photo}`
            : await getDefaultPlaceholderPhotoPath()
        } else {
          imageUrl = await getStudentPhotoPath(tutor)
          console.info('IMAGE URL: ', imageUrl)
          const isImageUrlValid = await checkUrl(imageUrl)
          console.info('IS IMAGE URL VALID: ', isImageUrlValid)

          if (!isImageUrlValid) imageUrl = await getDefaultPlaceholderPhotoPath()
        }

        // Match shift by epoch millis + summary (ISO strings can vary by format/timezone suffix)
        const eventStartMs = DateTime.fromISO(event.startISO).toMillis()
        const shift = todayShifts.find(shift => {
          if (!shift.start_time_iso) return false
          const shiftStartMs = DateTime.fromISO(shift.start_time_iso).toMillis()
          return shiftStartMs === eventStartMs && shift.summary?.trim() === event.summary?.trim()
        })
        console.info('MATCHED SHIFT: ', shift)

        const profile: SessionProfile = {
          tutor,
          event: {
            ...event,
            shift_id: shift?.shift_id,
            shift_status: shift?.shift_status,
          },
          imageUrl,
          inputDateTimeIso: inputDateTime.toUTC().toISO() as string,
          shift,
        }

        profiles.push(profile)
      }

      console.info('EFFECT > PROFILES CREATED: ', profiles)
      setProfiles(profiles)
      setProfilesPrepared(true)
    }

    createProfiles()
  }, [tutors])

  // Handle shift row updates
  useEffect(() => {
    console.info('ON DUTY > UPDATED ROW: ', updatedRow)

    // Find the profile with shift with matching email and start_time
    const matchIndex = profiles.findIndex(profile => {
      return (
        profile.shift?.email === updatedRow?.email &&
        profile.shift?.start_time === updatedRow?.start_time
      )
    })

    const newProfiles = [...profiles] // shallow copy!!! (in case React doesn't detect the change)

    const profile = newProfiles[matchIndex]
    if (profile?.shift) {
      profile.shift.shift_status = updatedRow?.shift_status
      setProfiles(newProfiles)
    }
  }, [updatedRow])

  useEffect(() => {
    // if (profiles.length > 0) setLoading(false)
    setLoading(false)
  }, [profiles])

  console.info('ON DUTY > PROFILES: ', profiles)
  // console.info('ON DUTY > PROFILES LENGTH: ', profiles.length)

  console.info(
    jsonDump({
      loading,
      profilesPrepared,
      profilesLength: profiles.length,
    })
  )

  return loading ? (
    <Box className={classes.loading}>
      <CircularProgress />
    </Box>
  ) : (
    <div className={classes.root}>
      <Helmet>
        <title>Tutors On Duty Now</title>
      </Helmet>
      <div className={classes.content}>
        <div className={classes.tutorContainer}>
          <div className={classes.tutorDisplay} onClick={() => console.info(dateTimeDisplay)}>
            {profiles.length === 1 ? (
              <SingleTutor profiles={profiles} />
            ) : profiles.length === 2 ? (
              <DoubleTutor profiles={profiles} />
            ) : profiles.length === 3 ? (
              <TripleTutor profiles={profiles} />
            ) : profiles.length === 4 ? (
              <QuadTutor profiles={profiles} />
            ) : profiles.length === 5 ? (
              <FiveTutor profiles={profiles} />
            ) : (
              <Tooltip
                title={<span style={{ whiteSpace: 'pre-line' }}>{getVersionTooltip()}</span>}
                placement="bottom"
                slotProps={{ tooltip: { sx: { fontFamily: 'monospace', whiteSpace: 'pre' } } }}
              >
                <div>
                  <Welcome error={contentsError} />
                </div>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default OnDuty
